import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Home } from 'lucide-react';
import Button from '../components/ui/Button.tsx';
import GradientText from '../components/ui/GradientText.tsx';
import SEO from '../components/SEO.tsx';

interface NotFoundProps {
  onNavigate: (page: string) => void;
}

const NotFound: React.FC<NotFoundProps> = ({ onNavigate }) => {
  const { t } = useTranslation();

  return (
    <>
      <SEO 
        title="404 - Page Not Found"
        description="The page you're looking for doesn't exist."
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex items-center justify-center px-4 sm:px-6 lg:px-8 2xl:px-12">
        <div className="max-w-2xl 2xl:max-w-4xl mx-auto text-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6 sm:space-y-8"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="text-6xl sm:text-8xl md:text-9xl lg:text-[10rem] xl:text-[12rem] 2xl:text-[14rem] font-bold leading-none">
                <GradientText>404</GradientText>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-4"
            >
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl 2xl:text-5xl font-bold text-gray-900">
                <GradientText>{t('notFound.title')}</GradientText>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-md lg:max-w-lg 2xl:max-w-xl mx-auto leading-relaxed">
                {t('notFound.description')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex justify-center"
            >
              <Button
                onClick={() => onNavigate('home')}
                variant="primary"
                size="lg"
                leftIcon={<Home className="w-5 h-5" />}
                className="w-full sm:w-auto px-8 py-4 text-base lg:text-lg"
              >
                {t('common.buttons.backToHome')}
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="pt-6 sm:pt-8 border-t border-gray-200"
            >
              <p className="text-sm lg:text-base text-gray-500 mb-4">{t('notFound.backOnTracks')}</p>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                {[
                  { name: 'Deploy', key: 'deploy' },
                  { name: 'Pricing', key: 'plans' },
                  { name: 'Roadmap', key: 'roadmap' },
                  { name: 'Contact', key: 'contact' }
                ].map((item) => (
                  <button
                    key={item.key}
                    onClick={() => onNavigate(item.key)}
                    className="text-sm lg:text-base text-primary hover:text-primary/80 transition-colors hover:underline px-2 py-1"
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default NotFound;